/**
 * Full-screen modal for claiming a daily reward.
 *
 * Views: chooser → (pick | spin) → claimed. Mirrors the structure of
 * AccountScreen — fixed-inset overlay, in-place re-render on view change.
 */

import type { StorageManager, ActiveReward, DailyRewardState } from '@/storage/save-data';
import {
  REWARD_CATALOG,
  rewardById,
  buildActiveReward,
  pickWeightedReward,
  pickWheelRewards,
  type RewardDef,
} from '@/game/daily-rewards';

type View = 'chooser' | 'pick' | 'spin' | 'claimed';

const HISTORY_LIMIT = 6;

export class DailyRewardScreen {
  private element: HTMLElement | null = null;
  private view: View = 'chooser';
  private claimedReward: ActiveReward | null = null;

  onClose: (() => void) | null = null;
  onClaimed: ((reward: ActiveReward) => void) | null = null;

  /** Test hook — when true, spin animation is skipped (immediate claim). */
  testSkipSpin = false;

  private spinWedges: RewardDef[] = [];
  private spinWinner: RewardDef | null = null;
  private spinAnimating = false;
  private spinTimers: number[] = [];
  private spinAbort: AbortController | null = null;

  constructor(
    private storage: StorageManager,
    private nowFn: () => number = () => Date.now(),
    private rng: () => number = Math.random,
    private sfx: { wheelTick(): void; rewardFanfare(): void } | null = null,
  ) {}

  show(): void {
    this.cancelSpin();
    this.view = 'chooser';
    this.claimedReward = null;
    this.spinWedges = [];
    this.spinWinner = null;
    this.render();
  }

  hide(): void {
    this.cancelSpin();
    this.element?.remove();
    this.element = null;
  }

  // ─── Render ────────────────────────────────────────────────

  private render(): void {
    if (this.element) this.element.remove();
    const el = document.createElement('div');
    el.id = 'daily-reward-screen';
    el.style.cssText = [
      'position:fixed;inset:0;',
      'background:rgba(10,10,26,0.95);',
      'display:flex;flex-direction:column;',
      'align-items:center;justify-content:flex-start;',
      'padding:3vh 16px;z-index:60;',
      'backdrop-filter:blur(8px);',
      "font-family:'Courier New',monospace;color:#ccc;",
      'overflow-y:auto;',
    ].join('');

    el.innerHTML = `
      <style>
        #daily-reward-screen h1 {
          color:#00ff66;font-size:clamp(1.2rem,4vw,1.6rem);font-weight:bold;
          text-shadow:0 0 20px #00ff66;letter-spacing:0.15em;margin:0 0 1rem;
        }
        #daily-reward-screen .dr-big {
          min-height:64px;padding:14px 28px;font:inherit;font-size:1rem;
          background:#00ff6622;border:2px solid #00ff66;color:#00ff66;
          border-radius:6px;cursor:pointer;letter-spacing:0.1em;
          margin:8px;min-width:220px;
        }
        #daily-reward-screen .dr-grid {
          display:grid;grid-template-columns:repeat(3,1fr);gap:10px;
          width:100%;max-width:360px;
        }
        #daily-reward-screen .dr-card {
          background:rgba(0,255,102,0.05);border:1px solid #00ff6644;
          border-radius:6px;padding:10px;text-align:center;cursor:pointer;
          color:#ccc;
        }
        #daily-reward-screen .dr-card:hover { background:rgba(0,255,102,0.15); }
        #daily-reward-screen .dr-card-icon { font-size:1.4rem;line-height:1.6rem; }
        #daily-reward-screen .dr-card-name { font-size:0.7rem;color:#aaa;margin-top:4px; }
        #daily-reward-screen .dr-link {
          color:#88aa99;font-size:0.8rem;cursor:pointer;background:none;
          border:none;text-decoration:underline;margin-top:1.4rem;padding:6px;
        }
      </style>
      ${this.renderView()}
    `;
    document.body.appendChild(el);
    this.element = el;
    this.wireEvents(el);
  }

  private renderView(): string {
    switch (this.view) {
      case 'chooser': return this.renderChooser();
      case 'pick':    return this.renderPick();
      case 'spin':    return this.renderSpin();
      case 'claimed': return this.renderClaimed();
    }
  }

  private renderChooser(): string {
    return `
      <h1>DAILY REWARD</h1>
      <div style="display:flex;flex-direction:column;align-items:center">
        <button class="dr-big" data-act="go-pick">CHOOSE A REWARD</button>
        <button class="dr-big" data-act="go-spin">SPIN THE WHEEL</button>
        <button class="dr-link" data-act="close">Maybe later</button>
      </div>
    `;
  }

  private renderPick(): string {
    const cards = REWARD_CATALOG.map((def) => `
      <div class="dr-card" data-act="pick-card" data-id="${def.id}">
        <div class="dr-card-icon">${rewardEmoji(def)}</div>
        <div class="dr-card-name">${escape(def.displayName)}</div>
      </div>
    `).join('');
    return `
      <h1>CHOOSE A REWARD</h1>
      <div class="dr-grid">${cards}</div>
      <button class="dr-link" data-act="go-chooser">← BACK</button>
    `;
  }

  private renderSpin(): string {
    // Sample the wedges and winner once per entry to the spin view.
    if (this.spinWedges.length === 0) {
      const history = this.storage.getDailyReward()?.history ?? [];
      this.spinWinner = pickWeightedReward(REWARD_CATALOG, history, this.rng);
      this.spinWedges = pickWheelRewards(this.spinWinner, REWARD_CATALOG, history, this.rng);
    }
    const N = this.spinWedges.length;
    const r = 110;
    const wedgePaths = this.spinWedges.map((def, i) => {
      const a0 = (i / N) * Math.PI * 2 - Math.PI / 2;
      const a1 = ((i + 1) / N) * Math.PI * 2 - Math.PI / 2;
      const x0 = Math.cos(a0) * r, y0 = Math.sin(a0) * r;
      const x1 = Math.cos(a1) * r, y1 = Math.sin(a1) * r;
      const fill = wedgeColor(i);
      const tx = Math.cos((a0 + a1) / 2) * (r * 0.65);
      const ty = Math.sin((a0 + a1) / 2) * (r * 0.65);
      return `
        <path data-wedge="${i}" d="M0 0 L${x0.toFixed(2)} ${y0.toFixed(2)} A${r} ${r} 0 0 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z"
              fill="${fill}" stroke="#0a0a1a" stroke-width="1"/>
        <text x="${tx.toFixed(2)}" y="${ty.toFixed(2)}" font-size="14" text-anchor="middle"
              dominant-baseline="middle" fill="#0a0a1a">${escape(rewardEmoji(def))}</text>
      `;
    }).join('');

    return `
      <h1>SPIN THE WHEEL</h1>
      <div style="position:relative;width:260px;height:260px;margin:1rem 0">
        <svg id="dr-wheel" viewBox="-130 -130 260 260" style="width:260px;height:260px;transform-origin:50% 50%;transition:transform 2.8s cubic-bezier(0.17,0.67,0.21,1.0)">
          ${wedgePaths}
          <circle r="6" fill="#fff"/>
        </svg>
        <div style="position:absolute;left:50%;top:-4px;transform:translateX(-50%);
                    width:0;height:0;border-left:10px solid transparent;border-right:10px solid transparent;
                    border-top:18px solid #ffcc33"></div>
      </div>
      <button class="dr-big" data-act="spin-start">SPIN</button>
      <button class="dr-link" data-act="go-chooser">← BACK</button>
    `;
  }

  private renderClaimed(): string {
    const ar = this.claimedReward;
    if (!ar) return this.renderChooser();
    const def = rewardById(ar.rewardId);
    return `
      <h1>YOU GOT</h1>
      <div style="color:#fff;font-size:1.4rem;margin:1rem 0">${escape(def?.displayName ?? ar.rewardId)}</div>
      <div style="font-size:3rem;margin-bottom:1rem">${def ? rewardEmoji(def) : '★'}</div>
      <button class="dr-big" data-act="claimed-ok">AWESOME</button>
    `;
  }

  // ─── Events ────────────────────────────────────────────────

  private wireEvents(root: HTMLElement): void {
    root.querySelectorAll<HTMLElement>('[data-act]').forEach((el) => {
      el.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
        this.handleAction(el.dataset.act!, el);
      });
    });
  }

  private handleAction(act: string, el?: HTMLElement): void {
    switch (act) {
      case 'close':
        this.cancelSpin();
        this.hide();
        this.onClose?.();
        return;
      case 'go-chooser':
        this.cancelSpin();
        this.view = 'chooser';
        this.spinWedges = [];
        this.spinWinner = null;
        this.render();
        return;
      case 'go-pick':
        this.view = 'pick'; this.render(); return;
      case 'go-spin':
        this.view = 'spin'; this.render(); return;
      case 'pick-card': {
        const id = el?.dataset.id ?? '';
        const def = rewardById(id);
        if (!def) return;
        this.claim(buildActiveReward(def));
        return;
      }
      case 'spin-start':
        this.startSpin();
        return;
      case 'claimed-ok':
        this.hide();
        this.onClose?.();
        return;
    }
  }

  private startSpin(): void {
    if (this.spinAnimating) return;
    const winner = this.spinWinner;
    if (!winner) return;
    const winnerIdx = this.spinWedges.findIndex((d) => d.id === winner.id);
    const wheel = this.element?.querySelector<SVGElement>('#dr-wheel');
    if (!wheel) return;
    const wedgeAngle = 360 / this.spinWedges.length;
    // The pointer is at the top (12 o'clock). To land wedge `winnerIdx` under
    // the pointer, the wheel must rotate so the wedge center sits at -90°.
    const wedgeCenter = winnerIdx * wedgeAngle + wedgeAngle / 2;
    const finalAngle = 360 * 6 + (270 - wedgeCenter); // 6 full spins + alignment

    const spinBtn = this.element?.querySelector<HTMLButtonElement>('[data-act="spin-start"]');
    if (spinBtn) spinBtn.disabled = true;

    if (this.testSkipSpin) {
      this.finishSpin();
      return;
    }

    this.spinAnimating = true;
    wheel.style.transform = `rotate(${finalAngle}deg)`;

    // Schedule wedge ticks during the spin. Density decreases over time to
    // mimic the wheel slowing down. We just play N evenly-spaced ticks across
    // the spin duration for v1 — good enough audibly.
    if (this.sfx) {
      const totalMs = 2800;
      const tickCount = 22;
      for (let i = 0; i < tickCount; i++) {
        // Quadratic spacing: faster at the start, slower at the end.
        const t = (i / tickCount) ** 1.6 * totalMs;
        this.spinTimers.push(window.setTimeout(() => this.sfx?.wheelTick(), t));
      }
    }

    this.spinTimers.push(window.setTimeout(() => this.finishSpin(), 2900));

    // Tap-to-skip: while spinning, tapping anywhere (except action buttons)
    // fast-forwards to the result.
    this.spinAbort = new AbortController();
    if (this.element) {
      this.element.addEventListener('pointerdown', (e) => {
        if (!this.spinAnimating) return;
        // Don't intercept taps on action buttons — let them handle navigation.
        const target = e.target as Element | null;
        if (target?.closest('[data-act]')) return;
        e.stopPropagation();
        for (const h of this.spinTimers) window.clearTimeout(h);
        this.spinTimers = [];
        this.finishSpin();
      }, { capture: true, signal: this.spinAbort.signal });
    }
  }

  private finishSpin(): void {
    this.spinAnimating = false;
    this.spinTimers = [];
    this.spinAbort?.abort();
    this.spinAbort = null;
    const winner = this.spinWinner;
    if (!winner) return;
    this.sfx?.rewardFanfare();
    this.claim(buildActiveReward(winner));
  }

  private cancelSpin(): void {
    for (const h of this.spinTimers) window.clearTimeout(h);
    this.spinTimers = [];
    this.spinAbort?.abort();
    this.spinAbort = null;
    this.spinAnimating = false;
  }

  /** Persist the reward to storage and transition to the claimed view. */
  private claim(reward: ActiveReward): void {
    const now = this.nowFn();
    const prev = this.storage.getDailyReward();
    const history = [reward.rewardId, ...(prev?.history ?? [])].slice(0, HISTORY_LIMIT);
    const state: DailyRewardState = { claimedAt: now, reward, history };
    this.storage.setDailyReward(state);
    this.claimedReward = reward;
    this.onClaimed?.(reward);
    this.view = 'claimed';
    this.render();
  }
}

function rewardEmoji(def: RewardDef): string {
  switch (def.kind) {
    case 'color':         return '●';
    case 'shape':
      switch (def.payload.kind === 'shape' ? def.payload.shape : 'equilateral') {
        case 'right-triangle': return '◢';
        case 'square':         return '◼';
        case 'pentagon':       return '⬟';
        case 'circle':         return '●';
        default:               return '▲';
      }
    case 'googly-eyes':   return '👀';
    case 'rainbow-trail': return '🌈';
  }
}

function escape(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]!));
}

function wedgeColor(i: number): string {
  const palette = ['#00ff6688', '#aa55ff88', '#ff339988', '#ffcc3388', '#88ff4488', '#ff884488', '#ffffff88', '#33aaff88'];
  return palette[i % palette.length];
}
